import React, { useState } from 'react';
import { Button, Stack } from 'react-bootstrap';
import { useNotification } from '../../hooks/NotificationContext';
import ConfirmationModal from '../elements/ConfirmationModal';
import { modifyUserAccountStatus } from '../../modules/users';
import AddUser from './AddUser';
import { TableRefreshButton } from '../elements/CustomButtons';

const UsersTableAction = ({
  fetchUsersData,
  setShowInactive,
  showInactive,
  seletectItems = [],
  currentUser,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openInvite, setOpenInvite] = useState(false);
  const { addNotification } = useNotification();

  const isOrgAdmin = currentUser?.role === 'org-admin';

  const handleOpenModal = (action) => {
    setModalAction(action);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setModalAction(null);
    setShowModal(false);
  };

  const handleConfirm = async () => {
    if (!modalAction || seletectItems.length === 0) return;

    setLoading(true);
    try {
      await modifyUserAccountStatus(seletectItems, modalAction);
      addNotification(
        'success',
        `Users ${
          modalAction === 'delete'
            ? 'deleted permanently'
            : modalAction === 'deactivate'
            ? 'deactivated'
            : 'reactivated'
        }!`
      );
      fetchUsersData();
    } catch (err) {
      addNotification(
        'danger',
        err?.message || `Failed to ${modalAction} users.`
      );
    }
    handleCloseModal();
    setLoading(false);
  };

  const confirmationText = {
    delete:
      'This will permanently delete the selected users. This action cannot be undone.',
    deactivate: 'Are you sure you want to deactivate the selected users?',
    restore: 'Are you sure you want to reactivate the selected users?',
  };

  const renderActionButtons = () => {
    if (!isOrgAdmin) return null;

    if (showInactive) {
      return (
        <>
          <Button
            variant="outline-secondary"
            disabled={seletectItems.length === 0}
            onClick={() => handleOpenModal('delete')}>
            Delete
          </Button>
          <Button
            variant="outline-secondary"
            disabled={seletectItems.length === 0}
            onClick={() => handleOpenModal('restore')}>
            Reactivate
          </Button>
        </>
      );
    } else {
      return (
        <Button
          variant="outline-secondary"
          disabled={seletectItems.length === 0}
          onClick={() => handleOpenModal('deactivate')}>
          Deactivate
        </Button>
      );
    }
  };

  return (
    <>
      <Stack direction="horizontal" gap={2}>
        {!showInactive && (isOrgAdmin || currentUser?.role === 'org-manager') && (
          <Button
            variant="primary"
            title="Invite members to your organization"
            onClick={() => setOpenInvite(true)}>
            + Invite Member
          </Button>
        )}

        {renderActionButtons()}

        <Button
          variant="outline-secondary"
          onClick={() => setShowInactive(!showInactive)}
          title={`Click to show ${showInactive ? 'Active' : 'Inactive'} Users`}>
          {showInactive ? 'Show Active' : 'Show Inactive'}
        </Button>
        <TableRefreshButton refreshData={fetchUsersData} />
      </Stack>

      <AddUser
        fetchUsersData={fetchUsersData}
        open={openInvite}
        setOpen={setOpenInvite}
      />

      <ConfirmationModal
        show={showModal}
        handleClose={handleCloseModal}
        handleConfirm={handleConfirm}
        title={`${modalAction?.charAt(0).toUpperCase()}${modalAction?.slice(
          1
        )} Users`}
        body={confirmationText[modalAction]}
        buttonLabel={loading ? 'Processing...' : 'Confirm'}
        buttonLabelSecondary="Cancel"
        buttonStyle="error"
        loading={loading}
      />
    </>
  );
};

export default UsersTableAction;
